import { isLightHexColor } from "~/utils/color";

export type InfosecLevelCssKey = "unclassified" | "cui" | "confidential" | "secret" | "top-secret" | "top-secret-sci";

const LEVEL_NAME_TO_CSS_KEY: Record<string, InfosecLevelCssKey> = {
  UNCLASSIFIED: "unclassified",
  CUI: "cui",
  CONFIDENTIAL: "confidential",
  SECRET: "secret",
  TOP_SECRET: "top-secret",
  TOP_SECRET_SCI: "top-secret-sci",
};

const US_DOD_LEVEL_DISPLAY_COLORS: Record<string, string> = {
  UNCLASSIFIED: "#007A33",
  CUI: "#502B85",
  CONFIDENTIAL: "#0033A0",
  SECRET: "#C8102E",
  TOP_SECRET: "#FF8C00",
  TOP_SECRET_SCI: "#FCE83A",
};

function normalizeLevelNameKey(levelName: string): string {
  const base = levelName
    .replace(/^NOTIONAL:\s*/i, "")
    .trim()
    .toUpperCase();
  return base.replace(/\s*-\s*/g, "_").replace(/\s+/g, "_");
}

function normalizeHexColor(hex: string): string {
  return hex.trim().toUpperCase();
}

function levelUsesUsDodThemeTokens(levelName: string, apiDisplayColor: string): boolean {
  const nameKey = normalizeLevelNameKey(levelName);
  const expected = US_DOD_LEVEL_DISPLAY_COLORS[nameKey];
  if (!expected) {
    return false;
  }
  return normalizeHexColor(apiDisplayColor) === normalizeHexColor(expected);
}

export function normalizeInfosecLevelCssKey(levelName: string): InfosecLevelCssKey | null {
  return LEVEL_NAME_TO_CSS_KEY[normalizeLevelNameKey(levelName)] ?? null;
}

export function resolveInfosecThemeBackground(levelName: string, apiDisplayColor: string): string {
  const key = normalizeInfosecLevelCssKey(levelName);
  if (!key || !levelUsesUsDodThemeTokens(levelName, apiDisplayColor)) {
    return apiDisplayColor;
  }
  return `var(--infosec-${key}, ${apiDisplayColor})`;
}

export function resolveInfosecThemeForeground(levelName: string, apiDisplayColor: string): string {
  const key = normalizeInfosecLevelCssKey(levelName);
  if (!key || !levelUsesUsDodThemeTokens(levelName, apiDisplayColor)) {
    return fallbackInfosecForeground(apiDisplayColor);
  }
  return `var(--infosec-${key}-foreground, ${fallbackInfosecForeground(apiDisplayColor)})`;
}

export function infosecLevelUsesBorder(levelName: string, apiDisplayColor: string): boolean {
  const key = normalizeInfosecLevelCssKey(levelName);
  if (key === "top-secret-sci" && levelUsesUsDodThemeTokens(levelName, apiDisplayColor)) {
    return true;
  }
  if (key && levelUsesUsDodThemeTokens(levelName, apiDisplayColor)) {
    return false;
  }
  return isLightHexColor(apiDisplayColor);
}

function fallbackInfosecForeground(apiDisplayColor: string): string {
  return isLightHexColor(apiDisplayColor) ? "var(--foreground)" : "#ffffff";
}
